import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";

import AgriPanel from "components/AgriTheme/AgriPanel";
import AgriStatCard from "components/AgriTheme/AgriStatCard";
import agriPaginationSx from "components/AgriTheme/agriPaginationSx";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import UserIdentityPreview from "components/UserIdentityPreview";
import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

const PERIODS = [
  { value: "day", label: "Day" },
  { value: "date_range", label: "Date Range" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
];
const VIEWS = [
  { value: "pyq_sets", label: "PYQ Sets" },
  { value: "pyq_questions", label: "PYQ Questions" },
  { value: "ar_levels", label: "AR Sets / Levels" },
  { value: "ar_questions", label: "AR Questions" },
];
const PAGE_SIZES = [10, 25, 50, 100];

const getCurrentValues = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return {
    day: `${year}-${month}-${day}`,
    week: `${year}-${month}-${day}`,
    month: `${year}-${month}`,
    year: String(year),
  };
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDuration = (seconds) => {
  const safeSeconds = Math.max(0, Number(seconds || 0));
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = safeSeconds % 60;
  return minutes ? `${minutes}m ${remainder}s` : `${remainder}s`;
};

const getUserImageUrl = (value) => {
  if (!value || String(value).startsWith("http")) return value || "";
  return `${Constants.imageDomain}${String(value).startsWith("/") ? "" : "/"}${value}`;
};

const getParentTitle = (parent, isPyq) => {
  if (!parent) return "-";
  if (isPyq) return [parent.exam_name, parent.year].filter(Boolean).join(" • ") || "PYQ";
  return [parent.subject_name, parent.topic_name].filter(Boolean).join(" > ") || "AR";
};

const getParentSubtitle = (parent, isPyq) => {
  if (!parent) return "";
  if (isPyq) {
    return [parent.exam_list_name, parent.shift, parent.set_name].filter(Boolean).join(" • ");
  }
  return [parent.level_name, parent.level_type].filter(Boolean).join(" • ");
};

const outcomeSx = (outcome) => {
  const colors = {
    correct: ["#4ade80", "rgba(34,197,94,0.13)"],
    wrong: ["#fb7185", "rgba(244,63,94,0.13)"],
    skipped: ["#fbbf24", "rgba(245,158,11,0.13)"],
  };
  const [color, backgroundColor] = colors[outcome] || colors.skipped;
  return { color, backgroundColor, border: `1px solid ${color}66`, fontWeight: 700 };
};

function LearningAttemptReports() {
  const currentValues = useMemo(getCurrentValues, []);
  const initialFilters = useMemo(
    () => ({
      period: "month",
      value: currentValues.month,
      start_date: currentValues.day,
      end_date: currentValues.day,
      search: "",
      outcome: "all",
    }),
    [currentValues]
  );
  const [view, setView] = useState("pyq_sets");
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState({ summary: {}, docs: [], pagination: {}, range: {} });
  const [notification, setNotification] = useState({ open: false, message: "" });

  const isPyq = view.startsWith("pyq_");
  const isQuestionView = view.endsWith("_questions");

  useEffect(() => {
    if (init() !== "success") return undefined;
    setLoading(true);
    axios
      .get(Constants.getUrls.getLearningAttemptReports, {
        params: {
          ...appliedFilters,
          view,
          outcome: isQuestionView ? appliedFilters.outcome : "all",
          start_date:
            appliedFilters.period === "date_range" ? appliedFilters.start_date : undefined,
          end_date: appliedFilters.period === "date_range" ? appliedFilters.end_date : undefined,
          page,
          limit: rowsPerPage,
        },
      })
      .then((response) => setReport(response?.data?.data || {}))
      .catch((error) =>
        setNotification({
          open: true,
          message: error?.response?.data?.message || "Unable to load PYQ and AR attempts.",
        })
      )
      .finally(() => setLoading(false));
    return undefined;
  }, [appliedFilters, isQuestionView, page, rowsPerPage, view]);

  const handlePeriodChange = (period) => {
    setFilters((current) => ({
      ...current,
      period,
      value: period === "date_range" ? current.value : currentValues[period],
    }));
  };

  const changeView = (nextView) => {
    setView(nextView);
    setPage(1);
  };

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters({ ...filters });
  };

  const summary = report?.summary || {};
  const pagination = report?.pagination || {};

  return (
    <DashboardLayout>
      <DashboardNavbar title="PYQ & AR Attempts" />

      <MDBox mb={1.5} display="flex" gap={0.75} flexWrap="wrap">
        {VIEWS.map((item) => (
          <MDButton
            key={item.value}
            size="small"
            color={view === item.value ? "info" : "secondary"}
            variant={view === item.value ? "gradient" : "outlined"}
            onClick={() => changeView(item.value)}
          >
            {item.label}
          </MDButton>
        ))}
      </MDBox>

      <MDBox mb={2} display="flex" gap={1.1} flexWrap="wrap" alignItems="center">
        <TextField
          select
          size="small"
          label="View By"
          value={filters.period}
          onChange={(event) => handlePeriodChange(event.target.value)}
          sx={{ minWidth: 135 }}
        >
          {PERIODS.map((item) => (
            <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
          ))}
        </TextField>
        {filters.period === "date_range" ? (
          <>
            <TextField
              size="small"
              type="date"
              label="From"
              value={filters.start_date}
              onChange={(event) =>
                setFilters((current) => ({ ...current, start_date: event.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              value={filters.end_date}
              onChange={(event) =>
                setFilters((current) => ({ ...current, end_date: event.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
          </>
        ) : (
          <TextField
            size="small"
            type={filters.period === "month" ? "month" : filters.period === "year" ? "number" : "date"}
            label={PERIODS.find((item) => item.value === filters.period)?.label}
            value={filters.value}
            onChange={(event) =>
              setFilters((current) => ({ ...current, value: event.target.value }))
            }
            InputLabelProps={{ shrink: true }}
          />
        )}
        {isQuestionView ? (
          <TextField
            select
            size="small"
            label="Outcome"
            value={filters.outcome}
            onChange={(event) =>
              setFilters((current) => ({ ...current, outcome: event.target.value }))
            }
            sx={{ minWidth: 125 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="correct">Correct</MenuItem>
            <MenuItem value="wrong">Wrong</MenuItem>
            <MenuItem value="skipped">Skipped</MenuItem>
          </TextField>
        ) : null}
        <TextField
          size="small"
          label="Search user name"
          value={filters.search}
          onChange={(event) =>
            setFilters((current) => ({ ...current, search: event.target.value }))
          }
          onKeyDown={(event) => { if (event.key === "Enter") applyFilters(); }}
          sx={{ minWidth: 190 }}
        />
        <MDButton color="info" size="small" onClick={applyFilters}>Apply</MDButton>
        <MDTypography variant="caption" sx={{ color: "#8fa7c4" }}>
          {report?.range?.start_date || "-"} to {report?.range?.end_date || "-"}
        </MDTypography>
      </MDBox>

      <Grid container spacing={1.15} mb={2.25}>
        {[
          ["history_edu", "PYQ Set Attempts", summary.pyq_attempts || 0, "linear-gradient(135deg,#0ea5e9,#075985)"],
          ["group", "PYQ Users", summary.pyq_unique_users || 0, "linear-gradient(135deg,#8b5cf6,#4c1d95)"],
          ["quiz", "PYQ Questions", summary.pyq_question_attempts || 0, "linear-gradient(135deg,#f59e0b,#92400e)"],
          ["rule", "AR Set Attempts", summary.ar_attempts || 0, "linear-gradient(135deg,#06b6d4,#164e63)"],
          ["groups", "AR Users", summary.ar_unique_users || 0, "linear-gradient(135deg,#22c55e,#14532d)"],
          ["fact_check", "AR Questions", summary.ar_question_attempts || 0, "linear-gradient(135deg,#f43f5e,#881337)"],
        ].map(([icon, label, value, tone]) => (
          <Grid item xs={6} md={4} xl={2} key={label}>
            <AgriStatCard icon={icon} label={label} value={value} tone={tone} compact dense />
          </Grid>
        ))}
      </Grid>

      <AgriPanel
        title={VIEWS.find((item) => item.value === view)?.label}
        subtitle="Names open user details. Contact information is not shown."
        noPadding
      >
        <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
          <Table
            size="small"
            sx={{
              minWidth: isQuestionView ? 1320 : 1100,
              tableLayout: "fixed",
              "& .MuiTableCell-root": {
                color: "#c7d8eb",
                borderBottom: "1px solid rgba(126,170,220,0.1)",
                fontSize: "0.72rem",
                padding: "9px 10px",
                verticalAlign: "middle",
              },
              "& .MuiTableHead-root": { display: "table-header-group", padding: 0 },
              "& .MuiTableHead-root .MuiTableCell-root": {
                color: "#fff",
                fontWeight: 700,
                background: "rgba(17,35,56,0.94)",
                whiteSpace: "nowrap",
              },
            }}
          >
            {isQuestionView ? (
              <colgroup>
                <col style={{ width: 55 }} /><col style={{ width: 190 }} />
                <col style={{ width: 315 }} /><col style={{ width: 235 }} />
                <col style={{ width: 90 }} /><col style={{ width: 105 }} />
                <col style={{ width: 105 }} /><col style={{ width: 70 }} />
                <col style={{ width: 150 }} />
              </colgroup>
            ) : (
              <colgroup>
                <col style={{ width: 55 }} /><col style={{ width: 210 }} />
                <col style={{ width: 275 }} /><col style={{ width: 90 }} />
                <col style={{ width: 80 }} /><col style={{ width: 80 }} />
                <col style={{ width: 80 }} /><col style={{ width: 90 }} />
                <col style={{ width: 80 }} /><col style={{ width: 150 }} />
              </colgroup>
            )}
            <TableHead>
              {isQuestionView ? (
                <TableRow>
                  <TableCell>#</TableCell><TableCell>User</TableCell><TableCell>Question</TableCell>
                  <TableCell>{isPyq ? "PYQ Set" : "AR Level"}</TableCell>
                  <TableCell>Outcome</TableCell><TableCell>User Answer</TableCell>
                  <TableCell>Correct Answer</TableCell><TableCell>Time</TableCell>
                  <TableCell>Attempted</TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell>#</TableCell><TableCell>User</TableCell>
                  <TableCell>{isPyq ? "PYQ Set" : "AR Level"}</TableCell>
                  <TableCell align="center">Questions</TableCell><TableCell align="center">Right</TableCell>
                  <TableCell align="center">Wrong</TableCell><TableCell align="center">Skipped</TableCell>
                  <TableCell align="center">Accuracy</TableCell><TableCell>Time</TableCell>
                  <TableCell>Attempted</TableCell>
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={isQuestionView ? 9 : 10}>Loading attempts...</TableCell></TableRow>
              ) : !report?.docs?.length ? (
                <TableRow><TableCell colSpan={isQuestionView ? 9 : 10}>No attempts found for this period.</TableCell></TableRow>
              ) : report.docs.map((row, index) => (
                <TableRow key={row._id} hover>
                  <TableCell>{(pagination.page - 1) * pagination.limit + index + 1}</TableCell>
                  <TableCell>
                    {row.user?._id ? (
                      <Link to={`/user-details/${row.user._id}`} style={{ textDecoration: "none" }}>
                        <UserIdentityPreview
                          name={row.user.name}
                          imageUrl={getUserImageUrl(row.user.profile_pic_url)}
                          subtitle={row.user.college}
                          imageSize={34}
                        />
                      </Link>
                    ) : (
                      <MDTypography variant="button" sx={{ color: "#fff" }}>Unknown User</MDTypography>
                    )}
                  </TableCell>
                  {isQuestionView ? (
                    <>
                      <TableCell>
                        <MDTypography
                          variant="caption"
                          sx={{
                            color: "#fff",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            lineHeight: 1.45,
                          }}
                        >
                          {row.assertion || row.reason
                            ? `Assertion: ${row.assertion || "-"} Reason: ${row.reason || "-"}`
                            : row.question || "Question snapshot unavailable"}
                        </MDTypography>
                      </TableCell>
                      <TableCell>
                        <MDTypography variant="button" fontWeight="bold" sx={{ color: "#fff", fontSize: "0.73rem" }}>
                          {getParentTitle(row.parent, isPyq)}
                        </MDTypography>
                        <MDTypography variant="caption" display="block" sx={{ color: "#8fa7c4" }}>
                          {getParentSubtitle(row.parent, isPyq)}
                        </MDTypography>
                      </TableCell>
                      <TableCell><Chip size="small" label={row.outcome} sx={outcomeSx(row.outcome)} /></TableCell>
                      <TableCell>{row.user_answer || "-"}</TableCell>
                      <TableCell>{row.correct_answer || "-"}</TableCell>
                      <TableCell>{formatDuration(row.time_spent_seconds)}</TableCell>
                      <TableCell>{formatDateTime(row.attempted_at)}</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>
                        <MDTypography variant="button" fontWeight="bold" sx={{ color: "#fff", fontSize: "0.73rem" }}>
                          {getParentTitle(row.parent, isPyq)}
                        </MDTypography>
                        <MDTypography variant="caption" display="block" sx={{ color: "#8fa7c4" }}>
                          {getParentSubtitle(row.parent, isPyq)}
                        </MDTypography>
                      </TableCell>
                      <TableCell align="center">{row.total_questions}</TableCell>
                      <TableCell align="center" sx={{ color: "#4ade80 !important", fontWeight: 700 }}>{row.right_attempt}</TableCell>
                      <TableCell align="center" sx={{ color: "#fb7185 !important", fontWeight: 700 }}>{row.wrong_attempt}</TableCell>
                      <TableCell align="center" sx={{ color: "#fbbf24 !important", fontWeight: 700 }}>{row.skip_attempt}</TableCell>
                      <TableCell align="center">{Number(row.accuracy_percent || 0).toFixed(2)}%</TableCell>
                      <TableCell>{formatDuration(row.time_spent_seconds)}</TableCell>
                      <TableCell>{formatDateTime(row.attempted_at)}</TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <MDBox px={2} py={1.5} display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
          <MDBox display="flex" alignItems="center" gap={1.2}>
            <MDTypography variant="caption" sx={{ color: "#8fa7c4" }}>Rows per page</MDTypography>
            <TextField
              select
              size="small"
              value={rowsPerPage}
              onChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(1); }}
              sx={{ width: 78 }}
            >
              {PAGE_SIZES.map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
            </TextField>
            <MDTypography variant="caption" sx={{ color: "#c7d8eb" }}>
              {pagination.totalDocs
                ? `${(pagination.page - 1) * pagination.limit + 1}-${Math.min(pagination.page * pagination.limit, pagination.totalDocs)} of ${pagination.totalDocs}`
                : "0 records"}
            </MDTypography>
          </MDBox>
          {pagination.totalPages > 1 ? (
            <Pagination
              page={pagination.page}
              count={pagination.totalPages}
              variant="outlined"
              shape="rounded"
              sx={agriPaginationSx}
              onChange={(_, nextPage) => setPage(nextPage)}
            />
          ) : null}
        </MDBox>
      </AgriPanel>

      <MDSnackbar
        color="warning"
        icon="notifications"
        title="AgriQuiz"
        content={notification.message}
        dateTime=""
        open={notification.open}
        onClose={() => setNotification({ open: false, message: "" })}
        close={() => setNotification({ open: false, message: "" })}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default LearningAttemptReports;
